import { createClient } from 'graphql-ws';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { RetryLink } from '@apollo/client/link/retry';
import { ApolloClient, ApolloError, ApolloLink, FetchResult, Observable, Operation, from, split } from '@apollo/client';
import type { ServerError } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';

import { createUploadLink } from 'apollo-upload-client';

import { toast } from 'react-toastify';

import { getApiUrl, getGraphQLUrl, getGraphQLWebSocketUrl } from './graphql-utils';
import { clearTokens, getAccessToken } from './storage';
import { cache } from './cache';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { logError, logInfo } from 'helpers/logger';
import {
    getSocketConnectionParams,
    onSocketClosed,
    onSocketConnected,
    restartSocket,
    shouldRetrySocket,
    waitBeforeSocketRetry,
} from './socket-session';
import { createTokenRefreshLink } from './token-refresh-link';

type ErrorLevel = 'error' | 'warning' | 'info';

// Configuration des erreurs
type ErrorConfig = {
    level: ErrorLevel;
    autoClose: number | false;
    showUser: boolean;
    redirectToLogin?: boolean;
};

const ERROR_CONFIG: Record<string, ErrorConfig> = {
    BAD_USER_INPUT: {
        level: 'warning',
        autoClose: false,
        showUser: true,
    },
    UNAUTHENTICATED: {
        level: 'error',
        autoClose: false,
        showUser: true,
        redirectToLogin: true,
    },
    AHEEX: {
        level: 'info',
        autoClose: 0,
        showUser: false,
    },
    // Compte archivé, supprimé ou banni : la session ne reviendra pas, déconnexion sans tenter de refresh.
    AHEARCHIVED: {
        level: 'error',
        autoClose: false,
        showUser: false,
        redirectToLogin: true,
    },
    AHEBANNED: {
        level: 'error',
        autoClose: false,
        showUser: false,
        redirectToLogin: true,
    },
    FORBIDDEN: {
        level: 'error',
        autoClose: 8000,
        showUser: true,
    },
    INTERNAL_SERVER_ERROR: {
        level: 'error',
        autoClose: false,
        showUser: true,
    },
    NETWORK_ERROR: {
        level: 'error',
        autoClose: 10000,
        showUser: true,
    },
};

// Fonction utilitaire pour extraire le message d'erreur utilisateur
function getUserFriendlyMessage(error: any): string {
    return error.message || "Une erreur inattendue s'est produite";
}

// Fonction pour afficher les erreurs avec la bonne configuration
function displayError(error: any, errorCode: string): void {
    const config = ERROR_CONFIG[errorCode as keyof typeof ERROR_CONFIG] || ERROR_CONFIG.INTERNAL_SERVER_ERROR;

    if (config.showUser) {
        const message = getUserFriendlyMessage(error);

        switch (config.level) {
            case 'warning':
                toast.warning(message, { autoClose: config.autoClose });
                break;
            case 'info':
                toast.info(message, { autoClose: config.autoClose });
                break;
            default:
                toast.error(message, { autoClose: config.autoClose });
        }
    }

    // Gestion spéciale pour les erreurs d'authentification
    if (config.redirectToLogin) {
        clearTokens();
        window.location.href = '/login';
    }
}

// Le client ne remonte que ce que l'API ne peut pas voir ou rejette avant de l'exécuter : l'API capture déjà ses
// propres erreurs, et un refus métier n'est pas un bug.
const CONTRACT_ERROR_CODES = new Set(['GRAPHQL_PARSE_FAILED', 'GRAPHQL_VALIDATION_FAILED', 'BAD_USER_INPUT']);

type GraphQLErrorLike = {
    message: string;
    extensions?: Record<string, unknown>;
};

function errorCodeOf(error: GraphQLErrorLike): string {
    const code = error.extensions?.code;
    return typeof code === 'string' ? code : '';
}

// Une requête refusée en 400 arrive en erreur réseau, ses erreurs GraphQL dans le corps ; une subscription
// refusée à l'abonnement, en ApolloError.
function graphQLErrorsOf(networkError: unknown): ReadonlyArray<GraphQLErrorLike> {
    if (networkError instanceof ApolloError) {
        return networkError.graphQLErrors;
    }

    const result = (networkError as Partial<ServerError> | null)?.result;
    if (result && typeof result === 'object' && Array.isArray(result.errors)) {
        return result.errors;
    }

    return [];
}

function handleGraphQLError({
    error,
    operation,
    isSubscription,
}: {
    error: GraphQLErrorLike;
    operation: Operation;
    isSubscription: boolean;
}): void {
    const errorCode = errorCodeOf(error);
    const isUnauthenticated = errorCode === 'AHERA' || error.message === 'Authentification requise';

    // AHEEX and AHERA are handled by the token-refresh link above: refresh, replay, and
    // log out only once the server has refused the refresh token too. Surfacing them
    // here would spam toasts and Sentry, and logging out here would skip the refresh.
    if (errorCode === 'AHEEX' || isUnauthenticated) {
        return;
    }

    // Auth backend briefly unavailable on a subscription: the token-refresh link
    // resubscribes later. On HTTP the same failure is a 503, i.e. a network error.
    if (isSubscription && errorCode === 'AUTH_UNAVAILABLE') {
        return;
    }

    displayError(error, errorCode);

    const tags = { operationName: operation.operationName, code: errorCode };

    // Une subscription ne passe pas par le plugin Sentry de l'API : le client est seul à la voir échouer.
    if (isSubscription || CONTRACT_ERROR_CODES.has(errorCode)) {
        logError({
            message: `[GraphQLError] ${error.message}`,
            tags,
            contexts: {
                error,
                operationVariables: operation.variables,
            },
        });
        return;
    }

    logInfo({ message: `[GraphQLError] ${error.message}`, tags });
}

const graphQLWebSockerUrl = getGraphQLWebSocketUrl();

export const subscriptionClient = createClient({
    url: graphQLWebSockerUrl,
    connectionParams: getSocketConnectionParams,
    retryAttempts: Infinity,
    retryWait: waitBeforeSocketRetry,
    shouldRetry: shouldRetrySocket,
    on: {
        connected: onSocketConnected,
        closed: onSocketClosed,
    },
});

const wsLink = new GraphQLWsLink(subscriptionClient);

const graphQLUrl = getGraphQLUrl();

const uploadLink = createUploadLink({ uri: graphQLUrl });

const authLink = setContext((_, { headers }) => {
    const authorization = getAccessToken() || '';
    return {
        headers: {
            ...headers,
            'apollo-require-preflight': true, // https://www.apollographql.com/docs/apollo-server/security/cors/
            authorization,
        },
    };
});

// Transient network failures ("Failed to fetch" — a brief connectivity blip, VPN hiccup or a
// server restart) surface as a network error and otherwise bubble straight to the errorLink as a
// toast + Sentry report (see TOTADMIN-62E). Retry queries a few times so a momentary blip stays
// invisible to the user. Scoped to queries only: mutations may not be idempotent (retrying risks
// double-submits) and subscriptions reconnect through their own graphql-ws retry.
const retryLink = new RetryLink({
    attempts: {
        max: 3, // initial request + 2 retries
        retryIf: (error, operation) => {
            const definition = getMainDefinition(operation.query);
            const isQuery = definition.kind === 'OperationDefinition' && definition.operation === 'query';
            return Boolean(error) && isQuery;
        },
    },
    delay: {
        initial: 300,
        max: 3000,
        jitter: true,
    },
});

// we don't need httpLink because uploadLink already integrates it.
// Moreover, we can only use on terminating Link, and uploadLink is considered as a terminating Link (because of its httpLink)
export function createApolloClient() {
    const errorLink = new ApolloLink((operation, forward) => {
        return new Observable<FetchResult>((observer) => {
            const operationName = operation.operationName;
            const definition = getMainDefinition(operation.query);
            const isSubscription = definition.kind === 'OperationDefinition' && definition.operation === 'subscription';

            forward(operation).subscribe({
                next: (result) => {
                    result.errors?.forEach((error) => handleGraphQLError({ error, operation, isSubscription }));
                    observer.next(result);
                },
                error: (networkError: unknown) => {
                    const graphQLErrors = graphQLErrorsOf(networkError);

                    if (graphQLErrors.length) {
                        graphQLErrors.forEach((error) => handleGraphQLError({ error, operation, isSubscription }));
                    } else {
                        // Coupure isolée (Failed to fetch, socket fermée) : pas un bug. Une subscription se
                        // reconnecte seule, il n'y a rien à dire à l'utilisateur.
                        if (!isSubscription) {
                            displayError(networkError, 'NETWORK_ERROR');
                        }
                        logInfo({
                            message: `[NetworkError] ${networkError instanceof Error ? networkError.message : String(networkError)}`,
                            tags: { operationName },
                        });
                    }

                    observer.error(networkError);
                },
                complete: () => observer.complete(),
            });
        });
    });

    const removeTypenameLink = removeTypenameFromVariables();
    const apiUrl = getApiUrl();
    const tokenRefreshLink = createTokenRefreshLink(apiUrl, restartSocket);
    return new ApolloClient({
        cache,
        link: from([
            removeTypenameLink,
            tokenRefreshLink,
            errorLink,
            authLink,
            split(
                ({ query }) => {
                    const definition = getMainDefinition(query);
                    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
                },
                wsLink,
                // Retry transient network errors on the HTTP path only (subscriptions reconnect via
                // graphql-ws). Sits downstream of errorLink so a request that recovers on retry never
                // reaches the toast/Sentry handler — only a definitive failure does.
                from([
                    retryLink,
                    // TS issue : apollo-upload-client incorrect typing (https://github.com/DefinitelyTyped/DefinitelyTyped/issues/47369)
                    uploadLink as unknown as ApolloLink,
                ]),
            ),
        ]),
    });
}
